import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Download, FileText, HelpCircle } from 'lucide-react';

interface AdminViewProps {
  onBack: () => void;
}

export default function AdminView({ onBack }: AdminViewProps) {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* 헤더 */}
      <header className="gov-gradient text-white py-6 shadow-lg no-print">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="title-responsive font-bold">관리자용 전체 보기</h1>
              <p className="text-blue-100 mt-2">보수교육·면허신고 의사결정 트리 전체 구조</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                <Download className="w-4 h-4 mr-2" />
                PDF 저장
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onBack}
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                돌아가기
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* 메인 콘텐츠 */}
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto space-y-8">
          
          {/* 의사결정 플로우 차트 */}
          <Card className="gov-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                의사결정 플로우 차트
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                
                {/* 시작 */}
                <div className="text-center">
                  <div className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold">
                    시작
                  </div>
                  <div className="w-px h-8 bg-gray-300 mx-auto mt-2"></div>
                </div>

                {/* Q1 */}
                <div className="text-center">
                  <div className="inline-block bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold">
                    Q1: 최근 3년이내 면허신고 여부
                  </div>
                  <div className="flex justify-center mt-4 gap-8">
                    <div className="flex flex-col items-center">
                      <Badge variant="outline" className="mb-2">YES</Badge>
                      <div className="bg-red-500 text-white px-4 py-2 rounded text-sm">A1: 면허신고 미대상자</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <Badge variant="outline" className="mb-2">NO</Badge>
                      <div className="bg-green-500 text-white px-4 py-2 rounded text-sm">A2: 면허신고 대상자</div>
                      <div className="w-px h-8 bg-gray-300 mt-2"></div>
                    </div>
                  </div>
                </div>

                {/* Q2 */}
                <div className="text-center">
                  <div className="inline-block bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold">
                    Q2: 6개월 이상 업무 종사 여부
                  </div>
                  <div className="flex justify-center mt-4 gap-8">
                    <div className="flex flex-col items-center">
                      <Badge variant="outline" className="mb-2">YES</Badge>
                      <div className="bg-green-500 text-white px-4 py-2 rounded text-sm">A3: 보수교육 이수 대상자</div>
                      <div className="w-px h-8 bg-gray-300 mt-2"></div>
                    </div>
                    <div className="flex flex-col items-center">
                      <Badge variant="outline" className="mb-2">NO</Badge>
                      <div className="bg-green-500 text-white px-4 py-2 rounded text-sm">A4: 면제·유예·비대상 신청 대상자</div>
                      <div className="w-px h-8 bg-gray-300 mt-2"></div>
                    </div>
                  </div>
                </div>

                {/* Q3 & Q4 */}
                <div className="flex justify-center gap-16">
                  <div className="text-center">
                    <div className="bg-orange-500 text-white px-4 py-2 rounded font-semibold">
                      Q3: 보수교육 이수 여부
                    </div>
                    <div className="flex justify-center mt-4 gap-4">
                      <div className="flex flex-col items-center">
                        <Badge variant="outline" className="mb-2 text-xs">YES</Badge>
                        <div className="bg-blue-500 text-white px-3 py-1 rounded text-xs">A5: 면허신고센터 로그인</div>
                      </div>
                      <div className="flex flex-col items-center">
                        <Badge variant="outline" className="mb-2 text-xs">NO</Badge>
                        <div className="bg-blue-500 text-white px-3 py-1 rounded text-xs">A6: 이수현황 확인</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="bg-orange-500 text-white px-4 py-2 rounded font-semibold">
                      Q4: 신청대상연도
                    </div>
                    <div className="flex justify-center mt-4 gap-2">
                      <div className="flex flex-col items-center">
                        <Badge variant="outline" className="mb-2 text-xs">2014-2016</Badge>
                        <div className="bg-purple-500 text-white px-2 py-1 rounded text-xs">A7: 면제자</div>
                      </div>
                      <div className="flex flex-col items-center">
                        <Badge variant="outline" className="mb-2 text-xs">2017-2018</Badge>
                        <div className="bg-orange-500 text-white px-2 py-1 rounded text-xs">Q5</div>
                      </div>
                      <div className="flex flex-col items-center">
                        <Badge variant="outline" className="mb-2 text-xs">2019+</Badge>
                        <div className="bg-purple-500 text-white px-2 py-1 rounded text-xs">A8: 유예자</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 최종 단계들 */}
                <div className="text-center mt-8">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-indigo-500 text-white px-3 py-2 rounded text-sm">B1-B3: 면허신고 절차</div>
                    <div className="bg-teal-500 text-white px-3 py-2 rounded text-sm">C1-C3: 면제·유예·비대상 절차</div>
                    <div className="bg-cyan-500 text-white px-3 py-2 rounded text-sm">D1: 보수교육 이수</div>
                    <div className="bg-gray-500 text-white px-3 py-2 rounded text-sm">Summary: 완료</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 단계별 상세 정보 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* 질문 단계 */}
            <Card className="gov-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="w-5 h-5" />
                  질문 단계 (Q1-Q5)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="border-l-4 border-orange-500 pl-4">
                    <h4 className="font-semibold text-orange-700">Q1: 면허신고 여부</h4>
                    <p className="text-sm text-gray-600">최근 3년이내 면허신고를 하셨나요?</p>
                  </div>
                  <div className="border-l-4 border-orange-500 pl-4">
                    <h4 className="font-semibold text-orange-700">Q2: 업무 종사 여부</h4>
                    <p className="text-sm text-gray-600">신청연도에 6개월 이상 보건의료정보관리사 업무에 종사하였나요?</p>
                  </div>
                  <div className="border-l-4 border-orange-500 pl-4">
                    <h4 className="font-semibold text-orange-700">Q3: 보수교육 이수 여부</h4>
                    <p className="text-sm text-gray-600">보수교육을 이수하였나요?</p>
                  </div>
                  <div className="border-l-4 border-orange-500 pl-4">
                    <h4 className="font-semibold text-orange-700">Q4: 신청대상연도</h4>
                    <p className="text-sm text-gray-600">신청대상연도가 언제인가요?</p>
                  </div>
                  <div className="border-l-4 border-orange-500 pl-4">
                    <h4 className="font-semibold text-orange-700">Q5: 업무 복귀 여부</h4>
                    <p className="text-sm text-gray-600">업무 미종사 이후 1번 이상 업무에 종사하였나요?</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 결과 단계 */}
            <Card className="gov-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  결과 단계 분류
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="border-l-4 border-green-500 pl-4">
                    <h4 className="font-semibold text-green-700">A 단계 (A1-A10)</h4>
                    <p className="text-sm text-gray-600">기본 판정 및 안내 단계</p>
                  </div>
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h4 className="font-semibold text-blue-700">B 단계 (B1-B3)</h4>
                    <p className="text-sm text-gray-600">면허신고 절차 단계</p>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-purple-700">C 단계 (C1-C3)</h4>
                    <p className="text-sm text-gray-600">면제·유예·비대상 신청 단계</p>
                  </div>
                  <div className="border-l-4 border-teal-500 pl-4">
                    <h4 className="font-semibold text-teal-700">D 단계 (D1)</h4>
                    <p className="text-sm text-gray-600">보수교육 이수 단계</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* 증빙서류 안내 */}
          <Card className="gov-card">
            <CardHeader>
              <CardTitle>증빙서류 안내 요약</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="gov-table">
                  <thead>
                    <tr>
                      <th>신청 연도</th>
                      <th>구분</th>
                      <th>주요 대상자</th>
                      <th>필요 서류</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td rowSpan={3}>2014-2016년</td>
                      <td>면제</td>
                      <td>6개월 미만 종사자, 군복무자, 대학원생, 신규면허자</td>
                      <td>재직증명서, 면제확인증명서, 직무기술서 등</td>
                    </tr>
                    <tr>
                      <td>유예</td>
                      <td>질병 등 불가피한 사유</td>
                      <td>의사진단서 등</td>
                    </tr>
                    <tr>
                      <td rowSpan={3}>2017-2018년</td>
                      <td>비대상</td>
                      <td>업무 미종사자</td>
                      <td>재직증명서, 건강보험자격득실확인서 등</td>
                    </tr>
                    <tr>
                      <td>면제</td>
                      <td>업무 복귀자</td>
                      <td>재직증명서, 직무기술서 등</td>
                    </tr>
                    <tr>
                      <td rowSpan={2}>2019년 이후</td>
                      <td>면제</td>
                      <td>군복무자, 대학원생, 신규면허자, 출산자</td>
                      <td>병적증명서, 재학증명서, 면허증사본, 출산증명서 등</td>
                    </tr>
                    <tr>
                      <td>유예</td>
                      <td>6개월 이상 미종사자, 입원치료자, 해외근무자</td>
                      <td>재직증명서, 입퇴원확인서, 출입국확인서 등</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* 주요 링크 */}
          <Card className="gov-card">
            <CardHeader>
              <CardTitle>주요 링크 및 연락처</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-3">주요 사이트</h4>
                  <ul className="space-y-2 text-sm">
                    <li>• 면허신고센터: <a href="https://lic.kuksiwon.or.kr" className="text-blue-600 hover:underline">https://lic.kuksiwon.or.kr</a></li>
                    <li>• 협회 홈페이지: <a href="https://www.khima.or.kr" className="text-blue-600 hover:underline">https://www.khima.or.kr</a></li>
                    <li>• 온라인교육센터: <a href="https://khima.hullaro.com/userMain/goUserMain" className="text-blue-600 hover:underline">https://khima.hullaro.com/userMain/goUserMain</a></li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-3">문의처</h4>
                  <ul className="space-y-2 text-sm">
                    <li>• 대한보건의료정보관리사협회</li>
                    <li>• 증빙서류 안내: <a href="https://www.khima.or.kr/board/board.php?bo_table=01notice&wr_id=1606" className="text-blue-600 hover:underline">공지사항 참조</a></li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}